import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { invoicesApi, extractOne } from '../../api/facades';

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [showDelete, setShowDelete] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['invoices', numId],
    queryFn: () => invoicesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  const deleteMu = useMutation({
    mutationFn: () => invoicesApi.remove(numId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setShowDelete(false);
      navigate('/dashboard/invoices');
      toast.success('Invoice deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const handleDownload = async () => {
    if (Number.isNaN(numId)) return;
    try {
      setDownloading(true);
      const resPdf = await invoicesApi.downloadPdf(numId);
      const blob = new Blob([resPdf.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${(row as any)?.invoice_no ?? numId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  if (Number.isNaN(numId) || isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Invoice not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/dashboard/invoices')}>
          Back
        </Button>
      </div>
    );
  }

  const r = row as any;
  const items = (r?.invoice_items ?? r?.invoiceItems ?? []) as any[];

  const formatDate = (value: any) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200">{r?.invoice_no ?? 'Invoice'}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/dashboard/invoices')}>Back</Button>
          <Button variant="secondary" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Button variant="primary" onClick={() => navigate(`/dashboard/invoices/${id}/edit`)}>Edit</Button>
          <Button variant="secondary" onClick={() => setShowDelete(true)} className="text-red-600 hover:text-red-700">
            Delete
          </Button>
        </div>
      </div>
      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Client</dt>
              <dd className="text-slate-700 dark:text-slate-200">{r?.client?.name ?? r?.client_id ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Status</dt>
              <dd className="text-slate-700 dark:text-slate-200">{r?.inv_status ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Total</dt>
              <dd className="text-slate-700 dark:text-slate-200">
                {r?.total_amount != null ? `MWK ${Number(r.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Invoice date</dt>
              <dd className="text-slate-700 dark:text-slate-200">{formatDate(r?.inv_date)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Due date</dt>
              <dd className="text-slate-700 dark:text-slate-200">{formatDate(r?.due_date)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-slate-500 uppercase">Active</dt>
              <dd className="text-slate-700 dark:text-slate-200">{r?.is_active ?? '—'}</dd>
            </div>
            {r?.remarks && (
              <div className="md:col-span-2">
                <dt className="text-xs font-semibold text-slate-500 uppercase">Remarks</dt>
                <dd className="text-slate-700 dark:text-slate-200">{r.remarks}</dd>
              </div>
            )}
          </dl>
          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Items</div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800/80">
                  <tr>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-right w-20">Qty</th>
                    <th className="px-3 py-2 text-right w-24">Rate</th>
                    <th className="px-3 py-2 text-right w-24">Tax</th>
                    <th className="px-3 py-2 text-right w-28">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td className="px-3 py-4 text-center text-slate-500" colSpan={5}>No items.</td>
                    </tr>
                  ) : (
                    items.map((item: any, idx: number) => {
                      const amount = Number(item.item_amount ?? 0);
                      const tax = Number(item.tax ?? 0);
                      const lineTotal = amount + tax;
                      return (
                        <tr key={item.id ?? idx} className="border-t border-gray-200 dark:border-gray-700">
                          <td className="px-3 py-2">{item.item_description ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{item.iteam_qty ?? '—'}</td>
                          <td className="px-3 py-2 text-right">
                            {`MWK ${Number(item.item_rate ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {`MWK ${tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {`MWK ${lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <ConfirmDeleteModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={() => deleteMu.mutate()}
        title="Delete invoice"
        message="Are you sure? This cannot be undone."
        loading={deleteMu.isPending}
      />
    </div>
  );
};

export default InvoiceDetailPage;
